import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const LOGO = 'https://customer-assets.emergentagent.com/job_ebfcb4ad-e78d-4e26-be19-6338bba630f2/artifacts/tgcpznjt_Nexa_Logo%20icon%20cyan%20.png';

const Header = () => {
  const { t, toggleLanguage, language, isRTL } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { href: '#about', label: t('nav.about') },
    { href: '#services', label: t('nav.services') },
    { href: '#methodology', label: t('nav.approach') },
    { href: '#sectors', label: t('nav.sectors') },
    { href: '/products', label: t('nav.products'), isPage: true },
    { href: '#contact', label: t('nav.contact') },
  ];

  const goTo = (href) => {
    if (href.startsWith('/')) {
      window.location.href = href;
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 start-0 end-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-lg shadow-sm border-b border-slate-100' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-16">
        <a href="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <img src={LOGO} alt="Nexa" className="h-8 w-8" />
          <span className={`font-bold text-lg tracking-wider ${scrolled ? 'text-[#0A1628]' : 'text-white'}`}>NEXA</span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map(link => (
            <button key={link.href} onClick={() => goTo(link.href)}
              className={`text-sm transition-colors font-medium ${scrolled ? 'text-slate-600 hover:text-[#00B4D8]' : 'text-white/80 hover:text-white'}`}>
              {link.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button data-testid="language-toggle" onClick={toggleLanguage}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-widest uppercase border rounded-full transition-colors ${
              scrolled ? 'border-slate-200 bg-white/80' : 'border-white/30 bg-white/10'
            }`}>
            <Globe className={`w-3.5 h-3.5 ${scrolled ? 'text-slate-400' : 'text-white/60'}`} />
            <span className={language === 'en' ? 'text-[#00B4D8]' : (scrolled ? 'text-slate-400' : 'text-white/60')}>EN</span>
            <span className={scrolled ? 'text-slate-300' : 'text-white/30'}>/</span>
            <span className={language === 'ar' ? 'text-[#00B4D8]' : (scrolled ? 'text-slate-400' : 'text-white/60')}>AR</span>
          </button>

          <button onClick={() => goTo('#contact')} className="hidden sm:block text-sm font-semibold text-white bg-[#00B4D8] hover:bg-[#0094B3] px-5 py-2 rounded-lg transition-colors">
            {t('nav.quote')}
          </button>

          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-slate-600 hover:text-[#00B4D8]">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-b border-slate-100 shadow-lg">
          <nav className="flex flex-col gap-1 px-6 py-4">
            {navLinks.map(link => (
              <button key={link.href} onClick={() => goTo(link.href)}
                className="text-start py-2.5 text-sm text-slate-600 hover:text-[#00B4D8] transition-colors font-medium">
                {link.label}
              </button>
            ))}
            <button onClick={() => goTo('#contact')} className="mt-2 text-sm font-semibold text-white bg-[#00B4D8] hover:bg-[#0094B3] px-5 py-2.5 rounded-lg transition-colors">
              {t('nav.quote')}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
